import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, IconButton, Button, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const BalanceSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { spotBalances, futuresBalances, aiBalances, coinPrices } = useSelector((state) => state.assets);
  const [showBTC, setShowBTC] = useState(false);
  const [hideBalances, setHideBalances] = useState(false);

  // Balance calculations
  const totalSpotBalanceInUSDT = useMemo(() => {
    if (!spotBalances || !Array.isArray(spotBalances) || spotBalances.length === 0) return '0.00';
    return spotBalances.reduce((total, balance) => {
      const symbol = balance.asset_id?.symbol?.toLowerCase() || '';
      const priceInUSD = balance.asset_id?.symbol === 'USDT' ? 1 : coinPrices[symbol] || 0;
      const valueInUSD = (balance.balance || 0) * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [spotBalances, coinPrices]);

  const totalFuturesBalanceInUSDT = useMemo(() => {
    if (!futuresBalances || !Array.isArray(futuresBalances) || futuresBalances.length === 0) return '0.00';
    return futuresBalances.reduce((total, balance) => {
      const symbol = balance.asset_id?.symbol?.toLowerCase() || '';
      const priceInUSD = balance.asset_id?.symbol === 'USDT' ? 1 : coinPrices[symbol] || 0;
      const valueInUSD = (balance.balance || 0) * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [futuresBalances, coinPrices]);

  const totalAIBalanceInUSDT = useMemo(() => {
    if (!aiBalances || !Array.isArray(aiBalances) || aiBalances.length === 0) return '0.00';
    return aiBalances.reduce((total, balance) => {
      const symbol = balance.asset_id?.symbol?.toLowerCase() || '';
      const priceInUSD = balance.asset_id?.symbol === 'USDT' ? 1 : coinPrices[symbol] || 0;
      const valueInUSD = (balance.balance || 0) * priceInUSD;
      return total + valueInUSD;
    }, 0).toFixed(2);
  }, [aiBalances, coinPrices]);

  const totalBalanceInUSDT = useMemo(() => {
    return (
      parseFloat(totalSpotBalanceInUSDT || '0') +
      parseFloat(totalFuturesBalanceInUSDT || '0') +
      parseFloat(totalAIBalanceInUSDT || '0')
    ).toFixed(2);
  }, [totalSpotBalanceInUSDT, totalFuturesBalanceInUSDT, totalAIBalanceInUSDT]);

  const btcPrice = coinPrices['btc'] || 0;
  const totalBTC = btcPrice ? (totalBalanceInUSDT / btcPrice).toFixed(6) : '0.000000';

  const maskBalance = (balance, isBTC = false) => {
    const length = balance.toString().length;
    return '*'.repeat(Math.max(length, isBTC ? 8 : 6));
  };

  return (
    <Paper
      elevation={4}
      className="balance-section"
      sx={{
        p: isMobile ? 2 : 3,
        mb: 2,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.brand.main}15 100%)`,
        transition: 'all 0.3s ease',
        animation: 'fadeIn 0.5s ease-in-out',
        maxWidth: isMobile ? '100%' : 800,
        mx: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 'bold',
            fontFamily: "'Inter', 'Roboto', sans-serif",
          }}
        >
          Total Balance
        </Typography>
        <IconButton
          onClick={() => setHideBalances(!hideBalances)}
          sx={{ color: theme.palette.text.secondary }}
        >
          {hideBalances ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          className="balance-text"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 'bold',
            fontFamily: "'Inter', 'Roboto', sans-serif",
            transition: 'color 0.3s',
            '&:hover': { color: theme.palette.brand.main },
          }}
        >
          {hideBalances
            ? maskBalance(showBTC ? totalBTC : totalBalanceInUSDT, showBTC)
            : showBTC
            ? `${totalBTC} BTC`
            : `${totalBalanceInUSDT} USDT`}
        </Typography>
        <Button
          variant="contained"
          startIcon={<SwapVertIcon />}
          onClick={() => setShowBTC(!showBTC)}
          className="toggle-button"
          sx={{
            bgcolor: theme.palette.brand.main,
            color: theme.palette.text.primary,
            borderRadius: 2,
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            fontFamily: "'Inter', 'Roboto', sans-serif",
            px: isMobile ? 1.5 : 2,
            transition: 'all 0.3s',
            '&:hover': {
              bgcolor: `${theme.palette.brand.main}cc`,
              transform: 'translateY(-2px)',
              boxShadow: theme.shadows[2],
            },
          }}
        >
          {showBTC ? 'USDT' : 'BTC'}
        </Button>
      </Box>
      <Typography
        variant={isMobile ? 'body2' : 'body1'}
        sx={{
          color: theme.palette.text.secondary,
          mt: 1,
          fontFamily: "'Inter', 'Roboto', sans-serif",
        }}
      >
        ≈ {hideBalances
          ? maskBalance(showBTC ? totalBalanceInUSDT : totalBTC, !showBTC)
          : showBTC
          ? `${totalBalanceInUSDT} USDT`
          : `${totalBTC} BTC`}
      </Typography>
    </Paper>
  );
};

export default React.memo(BalanceSection);